import { useConfirm } from "@/components/DialogProvider";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useDeletedAgencies, useAgencyOperations } from "@/features/agencies/hooks/useAgencies";
import type { AgencyModel } from "@/firestore_types";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { Timestamp } from "firebase/firestore";
import { FileText, Inbox, Loader2, Mail, MapPin, Phone, RotateCcw, Users } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";

import { formatDateTime } from "@/api/datetime";

export const Route = createFileRoute("/dashboard/admin/trash/agencies")({
  component: RouteComponent,
});

function RouteComponent() {
  const { agencies, loading, error } = useDeletedAgencies();

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  return <DeletedAgenciesList agencies={agencies} />;
}

function DeletedAgenciesList({ agencies }: { agencies: AgencyModel[] }) {
  if (agencies.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="flex flex-col gap-6">
      {agencies.map((agency) => (
        <AgencyCard key={agency.id} agency={agency} />
      ))}
    </div>
  );
}

function AgencyCard({ agency }: { agency: AgencyModel }) {
  const emails = (agency.emails ?? []).filter((value) => value && value.trim().length > 0);
  const primaryEmail = emails[0] ?? "Not provided";
  const emailCount = Math.max(emails.length - 1, 0);

  const phones = (agency.phoneNumbers ?? []).filter((value) => value && value.trim().length > 0);
  const rawPrimaryPhone = phones[0] ?? "";
  const formattedPhone = rawPrimaryPhone ? formatPhoneNumber(rawPrimaryPhone) : "";
  const primaryPhone = formattedPhone || rawPrimaryPhone || "Not provided";
  const phoneCount = Math.max(phones.length - 1, 0);

  const contactCount = agency.contacts?.length ?? 0;
  const attachmentCount = agency.attachments?.length ?? 0;
  const deletedAt = agency.updatedAt ?? agency.createdAt;
  const deletedAtLabel = deletedAt ? formatTimestamp(deletedAt) : null;

  return (
    <Card className="border-muted/60">
      <CardHeader className="gap-4 md:flex md:items-center md:justify-between">
        <div className="space-y-2">
          <CardTitle className="text-lg">
            <Link
              to="/dashboard/admin/agencies/$agencyId"
              params={{ agencyId: agency.id }}
              className="cursor-pointer text-blue-600 transition-colors hover:text-blue-800 hover:underline"
            >
              {agency.name}
            </Link>
          </CardTitle>
          <CardDescription>{deletedAtLabel ? `Deleted ${deletedAtLabel}` : "Deleted recently"}</CardDescription>
        </div>
        <CardAction>
          <RestoreAgencyButton agency={agency} />
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <InfoBlock
          icon={<MapPin className="text-muted-foreground size-4" />}
          label="Address"
          value={formatAddress(agency.address) ?? "Not provided"}
        />
        <InfoBlock
          icon={<Mail className="text-muted-foreground size-4" />}
          label="Emails"
          value={formatPrimaryValue(primaryEmail, emailCount)}
        />
        <InfoBlock
          icon={<Phone className="text-muted-foreground size-4" />}
          label="Phone numbers"
          value={formatPrimaryValue(primaryPhone, phoneCount)}
        />
        <InfoBlock
          icon={<Users className="text-muted-foreground size-4" />}
          label="Contacts"
          value={contactCount ? `${contactCount} contact${contactCount > 1 ? "s" : ""}` : "None"}
        />
        <InfoBlock
          icon={<FileText className="text-muted-foreground size-4" />}
          label="Attachments"
          value={
            attachmentCount ? `${attachmentCount} attachment${attachmentCount > 1 ? "s" : ""}` : "None"
          }
        />
      </CardContent>
      <CardFooter className="text-muted-foreground text-xs">
        Restoring an agency will make it available to assign to workers again.
      </CardFooter>
    </Card>
  );
}

function RestoreAgencyButton({ agency }: { agency: AgencyModel }) {
  const { restoreAgency, loading } = useAgencyOperations();
  const confirm = useConfirm();

  const handleRestore = async () => {
    const confirmed = await confirm({
      title: "Restore agency",
      description: `"${agency.name}" will reappear in the agencies list for admins and managers.`,
    });

    if (!confirmed) return;

    try {
      await restoreAgency(agency.id);
      toast.success(`Restored "${agency.name}"`);
    } catch (error) {
      console.error("Failed to restore agency", error);
      toast.error("Failed to restore agency. Please try again.");
    }
  };

  return (
    <Button onClick={handleRestore} size="sm" variant="outline" disabled={loading}>
      {loading ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          Restoring...
        </>
      ) : (
        <>
          <RotateCcw className="size-4" />
          Restore
        </>
      )}
    </Button>
  );
}

function InfoBlock({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="border-muted flex flex-col gap-1 rounded-lg border border-dashed p-4">
      <span className="text-muted-foreground flex items-center gap-2 text-xs font-medium tracking-wide uppercase">
        {icon}
        {label}
      </span>
      <span className="text-foreground text-sm font-medium">{value}</span>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="border-muted/50 bg-muted/40 flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
      Loading deleted agencies...
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border-muted bg-muted/30 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-8 py-16 text-center">
      <Inbox className="text-muted-foreground size-10" />
      <div className="space-y-1">
        <p className="text-foreground text-base font-semibold">No deleted agencies</p>
        <p className="text-muted-foreground text-sm">
          Recently deleted agencies will appear here so you can restore them quickly.
        </p>
      </div>
    </div>
  );
}

function ErrorState({ error }: { error: unknown }) {
  return (
    <div className="border-destructive/30 bg-destructive/10 text-destructive flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-12 text-center text-sm">
      Failed to load deleted agencies.
      <code className="text-xs opacity-80">{String(error)}</code>
    </div>
  );
}

function formatPrimaryValue(primary: string, additionalCount: number) {
  if (!primary || primary === "Not provided") {
    return primary || "Not provided";
  }

  if (additionalCount > 0) {
    return `${primary} (+${additionalCount} more)`;
  }
  return primary;
}

function formatAddress(address?: AgencyModel["address"]) {
  if (!address) return null;
  const parts = [address.street, address.city, address.state, address.zipCode, address.country].filter(
    Boolean,
  );
  return parts.length > 0 ? parts.join(", ") : null;
}

function formatTimestamp(timestamp: Timestamp | null | undefined) {
  if (!timestamp) return null;
  return formatDateTime(timestamp.toDate());
}
